import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Gift, MapPin, Lightning, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

export default function Airdrops() {
    const [airdrops, setAirdrops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [selectedAirdrop, setSelectedAirdrop] = useState(null);
    
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef([]);
    const userMarkerRef = useRef(null);

    // Initialize map
    useEffect(() => {
        if (map.current) return;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setUserLocation({ lat: latitude, lng: longitude });
                initMap(longitude, latitude);
            },
            () => {
                // Default to Toronto
                setUserLocation({ lat: 43.6532, lng: -79.3832 });
                initMap(-79.3832, 43.6532);
            }
        );

        return () => {
            if (map.current) map.current.remove();
        };
    }, []);

    const initMap = (lng, lat) => {
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/dark-v11',
            center: [lng, lat],
            zoom: 14,
            attributionControl: false
        });

        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.current.on('load', () => {
            // Add user location marker
            const userEl = document.createElement('div');
            userEl.className = 'user-location-marker';
            userEl.style.position = 'relative';

            const dot = document.createElement('div');
            dot.style.cssText = 'width:16px;height:16px;background:#3B82F6;border:3px solid white;border-radius:50%;box-shadow:0 0 10px rgba(59,130,246,0.5);';
            const pulse = document.createElement('div');
            pulse.style.cssText = 'position:absolute;top:-4px;left:-4px;width:24px;height:24px;background:rgba(59,130,246,0.2);border-radius:50%;animation:pulse 2s infinite;';
            userEl.appendChild(pulse);
            userEl.appendChild(dot);
            
            userMarkerRef.current = new mapboxgl.Marker(userEl)
                .setLngLat([lng, lat])
                .addTo(map.current);

            // Add style for pulse animation
            const style = document.createElement('style');
            style.textContent = `
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    100% { transform: scale(2); opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        });
    };

    // Fetch airdrops when location is available
    useEffect(() => {
        if (userLocation) {
            fetchAirdrops();
        }
    }, [userLocation]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update map markers when airdrops change
    useEffect(() => {
        if (!map.current || !airdrops.length) return;

        // Clear existing markers
        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];

        // Add airdrop markers
        airdrops.forEach(airdrop => {
            const el = document.createElement('div');
            el.style.cssText = `
                width: 40px;
                height: 40px;
                background: linear-gradient(135deg, #FED800 0%, #F59E0B 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                box-shadow: 0 4px 12px rgba(254, 216, 0, 0.4);
                transition: transform 0.2s;
            `;
            const icon = document.createElement('span');
            icon.style.fontSize = '20px';
            icon.textContent = '\uD83C\uDF81';
            el.appendChild(icon);
            el.onmouseenter = () => { el.style.transform = 'scale(1.1)'; };
            el.onmouseleave = () => { el.style.transform = 'scale(1)'; };
            el.onclick = () => setSelectedAirdrop(airdrop);

            const marker = new mapboxgl.Marker(el)
                .setLngLat([airdrop.longitude, airdrop.latitude])
                .addTo(map.current);

            markersRef.current.push(marker);
        });

        // Fit map to show all airdrops
        if (airdrops.length > 0 && userLocation) {
            const bounds = new mapboxgl.LngLatBounds();
            bounds.extend([userLocation.lng, userLocation.lat]);
            airdrops.forEach(a => bounds.extend([a.longitude, a.latitude]));
            map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
        }
    }, [airdrops, userLocation]);

    const fetchAirdrops = async () => {
        try {
            const res = await fetch(
                `${API}/airdrops/nearby?lat=${userLocation.lat}&lng=${userLocation.lng}`,
                { credentials: 'include' }
            );
            if (res.ok) {
                setAirdrops(await res.json());
            }
        } catch (error) {
            console.error('Airdrops fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const claimAirdrop = async (airdrop) => {
        if (!userLocation) {
            toast.error('Location not available');
            return;
        }

        setClaiming(airdrop.airdrop_id);
        try {
            const res = await fetch(`${API}/airdrops/${airdrop.airdrop_id}/claim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    lat: userLocation.lat,
                    lng: userLocation.lng
                })
            });

            if (res.ok) {
                const result = await res.json();
                toast.success(result.message);
                setAirdrops(prev => prev.filter(a => a.airdrop_id !== airdrop.airdrop_id));
                setSelectedAirdrop(null);
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to claim airdrop');
            }
        } catch (error) {
            toast.error('Failed to claim airdrop');
        } finally {
            setClaiming(null);
        }
    };

    const getDistanceMeters = (airdrop) => {
        if (!userLocation) return Infinity;
        
        const R = 6371000;
        const dLat = (airdrop.latitude - userLocation.lat) * Math.PI / 180;
        const dLon = (airdrop.longitude - userLocation.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(userLocation.lat * Math.PI / 180) * Math.cos(airdrop.latitude * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    const getDistanceText = (airdrop) => {
        const dist = getDistanceMeters(airdrop);
        if (dist < 1000) {
            return `${Math.round(dist)}m away`;
        }
        return `${(dist / 1000).toFixed(1)}km away`;
    };

    const isInRange = (airdrop) => {
        return getDistanceMeters(airdrop) <= (airdrop.radius || 50);
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <span className="label-text text-primary text-glow">AIRDROPS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Collect Rewards</h1>
                    <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                        Find and claim Boltz airdrops near your location
                    </p>
                </motion.div>

                {/* Map */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rounded-xl border border-border overflow-hidden mb-6 relative"
                    style={{ height: '300px' }}
                >
                    <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
                    
                    <div className="absolute top-4 left-4 bg-background/90 backdrop-blur px-3 py-1.5 rounded-full z-10">
                        <span className="text-sm font-medium">{airdrops.length} airdrops nearby</span>
                    </div>
                </motion.div>

                {/* Selected Airdrop Popup */}
                {selectedAirdrop && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6"
                    >
                        <Card className={`glass-card ${isInRange(selectedAirdrop) ? 'border-primary' : ''}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-bold">{selectedAirdrop.description || 'Boltz Airdrop'}</h3>
                                        <p className="text-sm text-muted-foreground flex items-center mt-1">
                                            <MapPin size={14} className="mr-1" />
                                            {getDistanceText(selectedAirdrop)}
                                        </p>
                                    </div>
                                    <p className="font-mono font-bold text-primary text-lg">
                                        +{selectedAirdrop.value} Boltz
                                    </p>
                                </div>
                                
                                <div className="flex gap-2">
                                    {isInRange(selectedAirdrop) ? (
                                        <Button
                                            onClick={() => claimAirdrop(selectedAirdrop)}
                                            disabled={claiming === selectedAirdrop.airdrop_id}
                                            className="flex-1 btn-primary"
                                            data-testid="claim-airdrop-btn"
                                        >
                                            {claiming === selectedAirdrop.airdrop_id ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                                                    Claiming...
                                                </>
                                            ) : (
                                                <>
                                                    <Lightning size={16} className="mr-1" weight="fill" />
                                                    Claim Now
                                                </>
                                            )}
                                        </Button>
                                    ) : (
                                        <div className="flex-1 text-center py-2 text-sm text-muted-foreground">
                                            Get within {selectedAirdrop.radius || 50}m to claim
                                        </div>
                                    )}
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setSelectedAirdrop(null)}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Airdrops List */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <h2 className="font-bold mb-4">Nearby Airdrops</h2>
                    
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <Card key={i} className="glass-card">
                                    <CardContent className="p-4">
                                        <Skeleton className="h-20 w-full" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : airdrops.length === 0 ? (
                        <Card className="glass-card">
                            <CardContent className="p-8 text-center">
                                <Gift size={48} className="text-muted-foreground mx-auto mb-4" />
                                <p className="font-medium">No airdrops nearby</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Check back later or explore different areas
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {airdrops.map((airdrop, index) => {
                                const inRange = isInRange(airdrop);
                                return (
                                    <motion.div
                                        key={airdrop.airdrop_id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 * index }}
                                    >
                                        <Card 
                                            className={`glass-card cursor-pointer ${inRange ? 'border-primary' : ''}`}
                                            onClick={() => {
                                                setSelectedAirdrop(airdrop);
                                                map.current?.flyTo({
                                                    center: [airdrop.longitude, airdrop.latitude],
                                                    zoom: 16
                                                });
                                            }}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                            inRange ? 'bg-primary' : 'bg-muted'
                                                        }`}>
                                                            <Gift 
                                                                size={20} 
                                                                className={inRange ? 'text-primary-foreground' : 'text-muted-foreground'} 
                                                                weight="fill" 
                                                            />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-medium">
                                                                {airdrop.description || 'Boltz Airdrop'}
                                                            </h3>
                                                            <p className="text-sm text-muted-foreground">
                                                                {getDistanceText(airdrop)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <p className="font-mono font-bold text-primary">
                                                        +{airdrop.value} Boltz
                                                    </p>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </motion.div>

                {/* How It Works */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                >
                    <Card className="glass-card bg-muted/30">
                        <CardContent className="p-4">
                            <h3 className="font-bold text-sm mb-3 flex items-center">
                                <Gift size={16} className="mr-2 text-primary" />
                                How Airdrops Work
                            </h3>
                            <ul className="text-sm text-muted-foreground space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                    Airdrops appear randomly on the map
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                    Move within range to claim them
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                    Each airdrop can only be claimed once
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </Layout>
    );
}
